/* eslint-disable no-undef */
import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { v4 as uuidv4 } from "uuid";
import CollapsableFormSection from "controls/global/collapsable-form-section";
import {
  ActionResult,
  DialogRefType,
  FileUploadAdditionalInfo,
  FileUploadStatus,
  Message,
  PdfDocument,
  SCFile,
  TitleSearchProduct
} from "entities/UIModel";
import * as ApiModel from "entities/ApiModel";
import {
  ActionResultStatus,
  DocumentType,
  InternalTPS,
  MapActionType,
  OrderStatusType,
  OriginationSource,
  OriginationSourceMaskName,
  PERMISSIONS,
  ProductAction,
  ProductTypeName,
  ProgressImageType,
  TitleSearchProcessedStatusType,
  UIConstants,
} from "utils/data/enum";
import useFieldDisabler from "utils/custom-hooks/useFieldDisabler";
import {
  Box,
  debounce,
  Grid,
  styled
} from "@mui/material";
import TransactionTypeField from "./TransactionTypeField";
import ProductTypeField from "./ProductTypeField";
import CurrencyInputField from "controls/global/currency-field";
import NeedByDateField from "./NeedByDateField";
import TextInputField from "controls/global/text-input-field";
import PropertyTypeField from "./PropertyTypeField";
// import ProductAgencyLocationField from "../common/ProductAgencyLocationField";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import CustomTooltip from "controls/global/custom-tooltip";
import StyledInfoIcon from "theme/icons/StyledInfoIcon";
import {
  gaps,
  margin,
  padding
} from "theme/defaultStyle";
import UploadDropZone from "controls/global/upload-files/UploadDropZone";
import {
  FileRejection,
  FileWithPath
} from "react-dropzone";
import ActionButton from "controls/global/action-button";
import { showToast } from "controls/global/stewart-toast/showToast";
import StewartLink from "controls/global/stewart-link";
import useCreateFile from "utils/custom-hooks/useCreateFile";
import ConfirmationDialogWithProgressbar from "controls/global/dialogs/confirmation-dialog-with-progressbar";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import {
  getFormattedErrorMessage,
  mapFileUploaResponse
} from "pages/file/utils/fileUploadHelper";
import { useDocument } from "utils/context/DocumentContext";
import UploadFailedDialog from "./UploadFailedDialog";
import UploadedDocumentsGrid from "./UploadedDocumentsGrid";
import { ImageArchiveSearchCriteria } from "entities/ApiModel/ImageArchiveSearchCriteria";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { Document } from "entities/UIModel/Document";
import { useGlobalAccess } from "utils/context/GlobalAccessContext";
import { useMyFiles } from "utils/context/MyFilesContext";
import { Order } from "utils/sorting";
import { SelectionConfig } from "controls/global/stewart-table/StewartTable";
import { MultipleDownloadHeader } from "./MultipleDownloadHeader";
import ErrorDialog from "controls/global/dialogs/error-dialog";
import {
  capitalize,
  dateTimeAsFilename,
  downloadZipFile,
  // formatDateTime, 
  getBlobUrlForZipfile
} from "utils/shared";
import { TitleSearchGridCriteria } from "entities/UIModel/TitleSearchGridCriteria";
import {
  useTitleSearchActions,
  useTitleSearchGridItems,
  useTitleSearchGridStates,
  useTitleSearchMessageIntegrationKey
} from "utils/context/TitleSearchContext";
import MessageGrid from "./MessageGrid";
import ProductStatus from "./ProductStatus";
import NotificationEmailAddresses from "./NotificationEmailAddressField";
import { SendMessageDocument } from "./SendMessageDocument";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import PdfViewer from "controls/global/pdf-viewer";
import { getPdfDocument, getTitleSearchProductOptions, mapToUITitleSearch } from "pages/file/utils/helper";
import { useSpaceAgencyConfig } from "utils/context/SpaceContext";
import PrinterEyeIcon from "theme/icons/PrinterEyeIcon";
import CancelTitleSearchDialog from "./CancelTitleSearchDialog";
import CancelIcon from "@mui/icons-material/Block";
// import UndoIcon from "@mui/icons-material/Undo";
import OrderUpdateIcon from "theme/icons/OrderUpdateIcon";
import OrderUpdateDialog, { OrderUpdateDialogHandle } from "./OrderUpdateDialog";
import ResendIcon from "theme/icons/ResendIcon";
import { useInitialValuesFiles } from "utils/context/FilesContext";
import mapUiSCFileToApiSCFile from "pages/file/utils/toApi/mapUiSCFileToApiSCFile";
import { useTPS } from "utils/context/TPSContext";
import { useOrder } from "utils/context/OrderContext";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
import { CreateFileContext } from "pages/file/CreateFileContext";
import { useCompanyLocation } from "utils/context/CompanyLocationContext";
import { useWatch } from "react-hook-form";

const LOAN_AMOUNT_REQUIRED_MESSAGE = "Loan amount is required for";

const EmailTooltipContainer = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-end",
  paddingTop: padding.large2,
  gap: gaps.large1,
  "& .MuiInputBase-input": {
    paddingRight: padding.small2,
  },
  "& .MuiSvgIcon-root": {
    alignItems: "flex-end",
    display: "flex",
    height: "100%",
    marginRight: margin.zero,
    // paddingRight: padding.zero,
  },
});

const GridContainer = styled(Box)({
  "& .MuiGrid-root.MuiGrid-item": {
    paddingBottom: `${padding.medium1} !important`,
  },
  "& .drop-zone": {
    marginTop: 0,
  },
  "& .button": {
    textAlign: "right",
  }
});

const ButtonsContainer = styled(Box)({
  alignItems: "center",
  alignSelf: "center",
  display: "flex",
  flexDirection: "row",
  gap: gaps.small1,
  height: "100%",
  justifyContent: "flex-end",
  justifySelf: "flex-end",
});

interface Props {
  productIndex: number;
  titleSearchProduct: TitleSearchProduct;
  submitButtonDisabled: boolean;
  isTitleSearchDisabled: boolean;
  resendToStewartButtonDisabled: boolean;
  onSubmit: (integrationKey: string, productAction?: string) => Promise<ActionResult | undefined>;
  onDelete?: () => void;
  currentOrderUpdateKey?: string;
}

const EMAIL_MESSAGE = "For multiple email addresses, separate each one with a semicolon.";
const UPLOAD_SUCCESS_TITLE = "Document(s) successfully uploaded!";
const UPLOAD_FAIL_TITLE = "Document(s) upload failed!";
const UPLOAD_FAIL_MESSAGE = "View failed documents";
const NO_RECORD_MESSAGE = "There are currently no title search documents uploaded to this product";
const FETCHING_DATA_ERROR_MESSAGE = 'There was a problem fetching your documents. Please try again';
const RESEND_TO_TPS_SUCCESS_MESSAGE = "Title Search successfully routed to {tpsName}!";
const RESEND_TO_TPS_FAIL_MESSAGE = "Failed to submit to {tpsName}!";

const TitleSearch = ({
  productIndex,
  titleSearchProduct,
  onSubmit,
  onDelete,
  submitButtonDisabled,
  isTitleSearchDisabled = false,
  resendToStewartButtonDisabled,
  currentOrderUpdateKey
}: Props, ref: ForwardedRef<OrderUpdateDialogHandle>) => {

  const { rerenderFileInfo } = useContext(CreateFileContext);
  const { setValue, getValues, setError, setFocus } = useFormWrapper();
  const [key, setKey] = React.useState(0);
  const forceRerender = () => setKey(prev => prev + 1);
  const [, { getProductByOrderID }] = useOrder();

  const [{ initialValues },] = useInitialValuesFiles();
  const { reloadFile, reloadFileActivities, saveSCFile } = useCreateFile();
  const [, { titleSearchResend }] = useTPS();
  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] = useAutomaticProgressDialogActions();
  const [, { uploadFiles, getDownloadZipFile, getDocumentsByOrder, getTitleSearchOrderSummaryDocumentUrl }] = useDocument();
  const [, { updateSelectedTitleSearchGridItem, updateSelectedTitleSearchGridItems }] = useTitleSearchActions();
  const [{ isTitleSearchGridInEditMode }] = useTitleSearchGridStates();
  const tabTitle = `${ProductTypeName.TITLE_SEARCH} ${productIndex + 1}`;
  const disableDeleteButtom = useFieldDisabler("TitleSearchDeleteButton");
  const [isCancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCancelUpdateDialogOpen, setCancelUpdateDialogOpen] = useState(false);
  const [isOrderUpdateDialogOpen, setIsOrderUpdateDialogOpen] = useState(false);
  // Global Access Agency
  const [{ selectedAgency: globalAccessAgency }] = useGlobalAccess();
  const globalAccessAgencyID = globalAccessAgency?.[0]?.CompanyID;
  const [{ companyStateLocations }] = useCompanyLocation();

  // Permission
  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  const hasHideUnHideDocsAccess = permissionCodes?.includes(PERMISSIONS.TITLESEARCH_HIDEUNHIDEMESSAGES);
  const hasResendPermissionAccess = permissionCodes?.includes(PERMISSIONS.TITLESEARCH_RESEND);

  // upload states
  const [uploadCompleted, setUploadCompleted] = useState<boolean>(false);

  const isProgressCompletedRef = useRef<boolean>(false);
  const successUploadFilesRef = useRef<FileUploadStatus[]>([]);
  const failedUploadFilesRef = useRef<FileUploadStatus[]>([]);

  // control show progress 
  const [, { setBypassDefaultLoader, setPercentageComplete }] = useProcessStatusTracking();
  const [openProgressDialog, setOpenProgressDialog] = useState<boolean>(false);
  const [runProgress, setRunProgress] = useState<boolean>(false);
  const [progressRequestId, setProgressRequestId] = useState<string>("");

  // show upload failed dialog
  const uploadFailedDialogRef = useRef<DialogRefType>(null);

  //#region Uploaded Document Grid
  // configuration
  const { generalConfig } = useConfigContext();
  const [userInputDebounceTimeMs, setUserInputDebounceTimeMs] = useState<number>(750);
  const [, { setRequestTimeoutMs }] = useMyFiles();
  const InitialMessage = "Loading results...";
  const [data, setData] = useState<Document[]>();
  const [noRecordsMessage, setNoRecordsMessage] = useState<string[]>([InitialMessage]);
  const [filters, setFilters] = useState<ImageArchiveSearchCriteria | undefined>(undefined);
  const [sortColumn, setSortColumn] = useState<string | undefined>("-documentCreatedDate");
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof Document | undefined>("documentCreatedDate");
  const [partialFilterUpdate, setPartialFilterUpdate] = useState<boolean>(false);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const [readyToSearch, setReadyToSearch] = useState<boolean>(false);
  const [{ selectedTitleSearchGridItems }] = useTitleSearchGridItems();
  const [{ tsAgencyConfig }] = useSpaceAgencyConfig();
  const [isResendToTPSVisible, setIsResendToTPSVisible] = useState<boolean>(false);

  const currentProduct: TitleSearchProduct = getValues(`titleSearchProducts.${productIndex}`);
  const processedStatusTypeCode: string | undefined = currentProduct?.processedStatusTypeCode; //  getValues(`titleSearchProducts.${productIndex}.processedStatusTypeCode`); // titleSearchProduct.orderStatusTypeCode;
  const isProductStatusVisible: boolean = (processedStatusTypeCode && processedStatusTypeCode.toUpperCase() !== TitleSearchProcessedStatusType.SCFileCreated.toUpperCase()) ? true : false;
  const isSubmitVisible: boolean = !isProductStatusVisible;
  const isOrderUpdateVisible: boolean = [OrderStatusType.Completed.toString(), OrderStatusType.UpdateCompleted.toString(), OrderStatusType.UpdateCancelled.toString()].includes(currentProduct?.orderStatusTypeCode) ? true : false; //  currentProduct?.orderStatusTypeCode === OrderStatusType.Completed ? true : false;
  const isCancelTitleSearchVisible: boolean = isProductStatusVisible && [OrderStatusType.Pending.toString(), OrderStatusType.InProgress.toString()].includes(currentProduct?.orderStatusTypeCode);
  const isCancelOrderUpdateVisible: boolean = [OrderStatusType.UpdatePending.toString(), OrderStatusType.UpdateInProgress.toString()].includes(currentProduct?.orderStatusTypeCode); // currentProduct?.orderStatusTypeCode === OrderStatusType.UpdateInProgress;  && !!currentProduct?.orderLastUpdatedDate;
  const isResendToReswareVisible: boolean =
    // processedStatusTypeCode &&
    //   ( processedStatusTypeCode.toUpperCase() === TitleSearchProcessedStatusType.RWFailed.toUpperCase() ||
    //     ( processedStatusTypeCode.toUpperCase() === TitleSearchProcessedStatusType.Dropped2SB.toUpperCase() &&
    //       currentProduct?.lastErrorMessage
    //     )
    //   ) &&
    isProductStatusVisible &&
      !currentProduct?.externalFileNumber &&
      currentProduct?.orderStatusTypeCode === OrderStatusType.Pending
      ? true : false;

  const orderCreatedDate = currentProduct?.orderCreatedDate;
  const orderStatus = `${currentProduct?.orderStatusTypeName} ${[OrderStatusType.Voided.toString(), OrderStatusType.Cancelled.toString()].includes(currentProduct?.orderStatusTypeCode) && currentProduct?.orderCancelledDate
    ? currentProduct.orderCancelledDate.substring(0, 10)
    : currentProduct?.orderStatusTypeCode === OrderStatusType.Completed && currentProduct?.orderCompletedDate
      ? currentProduct.orderCompletedDate.substring(0, 10)
      : [OrderStatusType.UpdateCompleted.toString(), OrderStatusType.UpdateCancelled.toString()].includes(currentProduct?.orderStatusTypeCode) && currentProduct?.orderLastUpdatedDate
        ? currentProduct.orderLastUpdatedDate.substring(0, 10)
        : ""
    }`;

  const orderID = currentProduct?.orderID || 0;
  const integrationKey = currentProduct?.integrationKey;
  const tpsName = currentProduct?.tpsName;

  const isFileDisabled = useFieldDisabler("AddTitleSearchButton");

  // Determine Resend to TPS is visible
  const setResendToTPSButton = (data: Document[] | Message[] | undefined) => {
    const hasAnnyReswareDocuments =
      data?.some((d) =>
        d.originationSource ?
          [InternalTPS.Resware.toLowerCase(), OriginationSourceMaskName.StewartTitleProduction.toLowerCase()].includes(d.originationSource?.toLowerCase())
          : false
      ) || false;
    // const isOrderComplete = currentProduct?.orderStatusTypeCode === OrderStatusType.Completed;
    const isOriginatedByTPS = tpsName && tpsName.toLowerCase() !== OriginationSource.Default.toLowerCase() ? true : false;
    //setIsResendToTPSVisible(hasResendPermissionAccess && anyReswareDocuments && isOrderComplete && isOriginatedByTPS)
    setIsResendToTPSVisible(hasResendPermissionAccess && isOriginatedByTPS && hasAnnyReswareDocuments)
  }

  // Error dialog
  const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>("");
  const [correlationId, setCorrelationId] = React.useState<string>("");

  const [openPdfViewer, setOpenPdfViewer] = useState<boolean>(false);
  const [pdfDocuments, setPdfDocuments] = useState<PdfDocument[]>([]);

  const [{ messageIntegrationKey }, { setMessageIntegrationKey }] = useTitleSearchMessageIntegrationKey();

  const updateTitleSearchData = async (titleSearch: ApiModel.TitleSearchProduct) => {
    const updatedTitleSearch = mapToUITitleSearch(titleSearch);
    const currentPricingDetail = getValues(`titleSearchProducts.${productIndex}.pricingDetail`)
    updatedTitleSearch.pricingDetail = currentPricingDetail;
    setValue(`titleSearchProducts.${productIndex}`, updatedTitleSearch);
    await performSearch();
    forceRerender();
  }

  const handleRefresh = async () => {
    // Notes: getProductByOrderID does not return PricingDetail
    const actionResult: ActionResult = await getProductByOrderID(orderID)
    if (actionResult.status == ActionResultStatus.Success && actionResult.apiResponse) {
      const titleSearchLatest = actionResult.apiResponse as ApiModel.TitleSearchProduct;

      // test update
      // titleSearchLatest.OrderStatusTypeName = "NEW status here";
      updateTitleSearchData(titleSearchLatest)
      handleOnReloadFileActivities();
      rerenderFileInfo();
    }
  }

  useEffect(() => {
    if (currentOrderUpdateKey === currentProduct.integrationKey) {
      performSearch && performSearch();
      setValue(`titleSearchProducts.${productIndex}.productActionMessageText`, currentProduct!.messages![0].messageText);
      setValue(`titleSearchProducts.${productIndex}.messageNeedByDate`, currentProduct!.messages![0].needByDate);
    }
  }, [currentOrderUpdateKey]);

  const getCorrelationId = (httpResponseHeaders: any): string => {
    if (httpResponseHeaders && httpResponseHeaders["x-correlation-id"]) {
      return httpResponseHeaders["x-correlation-id"];
    }
    return "";
  };

  const handleOnYesErrorDialog = () => {
    setOpenErrorDialog(false);
  };

  // Checkbox handlers
  const handleToggleAllRows = async () => {
    if (data) {
      updateSelectedTitleSearchGridItems(data, orderID);
    }
  };

  const handleToggleRow = (document: Document) => {
    updateSelectedTitleSearchGridItem(document);
  };

  // download multiple documents into a zip file
  const handleDownloadZipfile = async () => {
    const selectedDocumentIds = selectedTitleSearchGridItems?.filter((d) => d.orderID === orderID)?.map((doc) => doc.documentID);
    const documentIds = selectedDocumentIds.join(",");

    const actionResult: ActionResult = await getDownloadZipFile(documentIds, DocumentType.TITLEDOCS);

    if (actionResult && actionResult.error) {
      setErrorMessage(UIConstants.DOWNLOAD_ZIPFILE_ERROR_MESSAGE);
      setCorrelationId(
        getCorrelationId(actionResult.error.response?.headers)
      );
      setOpenErrorDialog(true);
      return;
    }

    const blobUrl = await getBlobUrlForZipfile(actionResult.apiResponse);
    const fileName = `Title Search Documents Download ${dateTimeAsFilename()}.zip`;
    await downloadZipFile(blobUrl, fileName);
  }

  const selectionConfig: SelectionConfig<Document> = {
    enabled: isProductStatusVisible, //  && !isTitleSearchDisabled, 
    selectedRows:
      selectedTitleSearchGridItems?.filter((d) => d.orderID === orderID)?.map((d) => d.uniqueIdentifier) ?? [],
    onToggleAllRows: handleToggleAllRows,
    onToggleRow: handleToggleRow,
    isRowSelected: (document: Document) =>
      selectedTitleSearchGridItems?.filter((d) => d.orderID === orderID)?.some(
        (d) => d.uniqueIdentifier === document.uniqueIdentifier
      ),
  };

  const handleCancelSubmit = () => {
    setNotDefault();
    onSubmit(titleSearchProduct.integrationKey, ProductAction.Void);
    setCancelDialogOpen(false);
  };

  const handleCancelOrderUpdate = async () => {
    setValue(`titleSearchProducts.${productIndex}.messageIntegrationKey`, messageIntegrationKey || uuidv4());

    if (!messageIntegrationKey) {
      setMessageIntegrationKey(currentProduct.messageIntegrationKey);
    }

    setNotDefault();
    const actionResult = await onSubmit(titleSearchProduct.integrationKey, ProductAction.UndoRevise);
    if (actionResult && actionResult.status === ActionResultStatus.Success) {
      setCancelUpdateDialogOpen(false);
    }
  };

  const getDefaultFilters = (advanced: boolean): TitleSearchGridCriteria => {
    // const sortColumn = "-documentCreatedDate";

    const defaultFilters: TitleSearchGridCriteria = {
      fileId: getValues("id"),
      orderId: getValues(`titleSearchProducts.${productIndex}.orderID`),
      listCount: 0,
      requestedPage: 1,
      isInitialLoad: false,
      sortColumn: sortColumn,
    };
    return defaultFilters;
  };

  const getFileSearchCriteria = () => {
    let criteria: TitleSearchGridCriteria;

    criteria = {
      ...filters,
      listCount: 0,
      requestedPage: 1,
      ...(globalAccessAgencyID && { companyId: globalAccessAgencyID }),
    };
    if (sortColumn) {
      criteria = {
        ...criteria,
        sortColumn: sortColumn,
      };
    }

    return criteria;
  };

  const loadSettings = async () => {
    const debounceTime = generalConfig.userInputDebounceTimeMs;
    const timeoutMs = generalConfig.filesSearchTimeoutMs;

    if (debounceTime) {
      setUserInputDebounceTimeMs(debounceTime);
    }
    if (timeoutMs) {
      setRequestTimeoutMs(timeoutMs);
    }
  };

  const debounceSetReadyToSearch = useCallback(
    debounce(() => setReadyToSearch(true), userInputDebounceTimeMs),
    [userInputDebounceTimeMs],
  );

  const performSearch = async () => {
    let criteria = getFileSearchCriteria();
    criteria.orderId = getValues(`titleSearchProducts.${productIndex}.orderID`);
    criteria.showHidden = hasHideUnHideDocsAccess ? 1 : 0;
    if ((criteria.orderId || 0) > 0) {
      let documents: Document[] | undefined = [];
      try {
        documents = await getDocumentsByOrder({ ...criteria });
        if (documents !== undefined) {
          setData(documents);
          if (!documents?.length) {
            setNoRecordsMessage([NO_RECORD_MESSAGE]);
            // setPage(1);
          }
          // setValue(
          //   "hasUploadedPolicy",
          //   documents && documents.length > 0 ? true : false
          // );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast({
          type: "error",
          title: FETCHING_DATA_ERROR_MESSAGE,
        });
      }
    }
  };

  const handleRequestSort = async (columnName: any, disableToggling: boolean = false) => {
    let sortBy = columnName; // getDatabaseSortColumnName(columnName);
    if (!disableToggling && sortColumn === sortBy) {
      sortBy = `-${sortBy}`;
    }

    setOrder(sortBy.startsWith("-") ? "desc" : "asc");
    setOrderBy(columnName.replace("-", "") as keyof Document);
    setSortColumn(sortBy);
  };

  const canSearch = (): boolean => {
    return !partialFilterUpdate && filters !== undefined && Object.values(filters).length > 0 && !isTitleSearchGridInEditMode;
  };

  const handleOnReloadFile = async () => {
    const fileId = getValues("id");
    await reloadFile(fileId);  // happen after Save File
  }

  const handleOnReloadFileActivities = async () => {
    const fileId = getValues("id");
    await reloadFileActivities(fileId);  // happen after Save File
  }

  const handleOnSaveFile = async () => {
    await saveSCFile();
  }

  //#endregion

  // const showDelete = () => {
  //   if (disableDeleteButtom) return false;
  //   if (titleSearchProduct.orderStatusTypeCode === OrderStatusType.Pending) {
  //     return true;
  //   }
  //   return false;
  // };

  const getTabDescription = () => {
    return titleSearchProduct.stewartProduct;
  }

  const isFieldDisabled = isTitleSearchDisabled || isProductStatusVisible ; // && !reviseState.revising;  
  // console.log(`isFieldDesibled: - productIndex:${productIndex}`, isFieldDisabled);

  const setNotDefault = () => {
    setValue(`titleSearchProducts.${productIndex}.isDefault`, false);
  };

  const onNeedByDateChange = (date: Date | null) => {
    setNotDefault();
  }

  const showUploadFailedToast = () => {
    const handleLinkClick = () => {
      uploadFailedDialogRef.current?.open();
    }

    const linkMessage = () => {
      return (
        <StewartLink onClick={() => handleLinkClick()}>
          {UPLOAD_FAIL_MESSAGE}
        </StewartLink>
      );
    }

    showToast({
      type: 'error',
      title: UPLOAD_FAIL_TITLE,
      message: linkMessage(), // "The Property Type for your Title Search differs from the file Property Type. Please scroll to the top of the file to update."
    });
  }

  const handleOnUpload = async (acceptedFiles: FileWithPath[], rejectedFiles: FileRejection[]) => {
    // -- Show upload in progress 
    setUploadCompleted(false);
    const progressRequestId = uuidv4();
    setProgressRequestId(progressRequestId);
    setBypassDefaultLoader(true);
    setOpenProgressDialog(true);
    setRunProgress(true);

    isProgressCompletedRef.current = false;

    try {

      // Save file if file has not saved yet
      let fileId: number = getValues("id") || 0;
      let orderId: number = getValues(`titleSearchProducts.${productIndex}.orderID`) || 0;
      if (fileId === 0 || orderId === 0) {
        // set Title Search Product to Dirty because it also need to save TitleSearchProduct first
        setNotDefault();
        const actionResult = await saveSCFile();
        fileId = actionResult?.scfile?.id || 0;
        orderId = actionResult?.scfile?.titleSearchProducts[productIndex].orderID || 0;
      }

      if (fileId === 0 || orderId === 0) {
        setPercentageComplete(progressRequestId);
        setUploadCompleted(true);
        return;
      }

      // -- Prepare for upload
      let fileAdditionalInfos: FileUploadAdditionalInfo[] = [];
      const formData = new FormData();

      const uploadMessage: string = getValues("uploadMessage") || "";
      const agency = getValues("agency");
      const companyID = agency?.id || "";
      formData.set("companyID", companyID);
      formData.set("fileID", fileId.toString());
      formData.set("orderID", orderId.toString());
      // no messageID needed -- it will be always a new message for upload with message
      formData.set("messageText", uploadMessage);
      formData.set("processedStatusTypeCode", processedStatusTypeCode || "");

      // TEST ONLY DRAFT mode
      // formData.set("processedStatusTypeCode", TitleSearchProcessedStatusType.Dropped2SB);
      // formData.set("uploadSessionKey", uploadSessoinRef.current);

      // TEMPORARY ONLY needed for Document Suplementatal
      const clientFileID = getValues("fileNameNumber") || "";
      // const agency = getValues("agency");
      // const companyID = agency?.id || "";
      const companyLegacyID = agency?.legacyID || "";
      formData.set("clientFileID", clientFileID);
      // formData.set("companyID", companyID);
      formData.set("companyLegacyID", companyLegacyID);
      // END TEMPORARY

      let index = 0;
      acceptedFiles?.forEach((file) => {
        formData.append("files", file, file.name);
        fileAdditionalInfos.push({
          index: index,
          filePath: file.path || "",
          fileName: file.name,
          fileSize: file.size,
          errorMessage: "",
          isValidFile: true,
        })
        index++;
      })

      rejectedFiles?.forEach((rejectedFile, i) => {
        fileAdditionalInfos.push({
          index: index,
          filePath: "",  // not available
          fileName: rejectedFile.file.name,
          fileSize: rejectedFile.file.size,
          errorMessage: getFormattedErrorMessage(rejectedFile.errors),
          isValidFile: false,
        })
        index++;
      })

      formData.append("fileUploadAdditionalInfo", JSON.stringify(fileAdditionalInfos));



      // -- Start upload files 
      const actionResult: ActionResult = await uploadFiles(progressRequestId, formData, DocumentType.TITLEDOCS); // DocumentType.TITLESEARCHDOC);

      if (actionResult.status === ActionResultStatus.Success) {
        const uploadedResult = actionResult.apiResponse;
        const fileUploadStatuses = mapFileUploaResponse(uploadedResult);

        // -- show upload result status
        const hasSuccess = fileUploadStatuses?.some((f) => f.isUploadSuccess);
        const hasFailed = fileUploadStatuses?.some((f) => !f.isUploadSuccess);
        successUploadFilesRef.current = fileUploadStatuses?.filter((f) => f.isUploadSuccess);
        failedUploadFilesRef.current = fileUploadStatuses?.filter((f) => !f.isUploadSuccess);

        if (hasFailed) {
          showUploadFailedToast();
        }
        if (hasSuccess) {
          showToast({
            type: 'success',
            title: UPLOAD_SUCCESS_TITLE,
          });

          // reset order by DocumentCreatedDate
          setOrder("desc");
          setOrderBy("documentCreatedDate" as keyof Document);
          setSortColumn("-documentCreatedDate");
          await performSearch();
        }
      }
      else {
        showToast({
          type: 'error',
          title: UIConstants.UNEXPECTED_ERROR_MESSAGE.toString(),
        });
      }

      setPercentageComplete(progressRequestId);
      setUploadCompleted(true);
    }
    catch (err) {

      setPercentageComplete(progressRequestId);
      setUploadCompleted(true);
      // console.log("upload error:", err)
      showToast({
        type: 'error',
        title: UPLOAD_FAIL_TITLE
      });
    }

  }

  const transactionType = getValues(`titleSearchProducts.${productIndex}.transactionType`);
  const propertyTypeCode = getValues(`titleSearchProducts.${productIndex}.propertyTypeCode`);
  const stewartProduct = getValues(`titleSearchProducts.${productIndex}.stewartProduct`);

  const [loanAmount] = useWatch({
      name: [`titleSearchProducts.${productIndex}.loanAmount`],
  });

  // The saved product may be on screen only as a disabled option (the agency config no
  // longer offers it) — it stays visible, but it must not be ordered.
  const isStewartProductSelectable = getTitleSearchProductOptions(
    tsAgencyConfig,
    transactionType,
    propertyTypeCode,
    stewartProduct
  ).some((option) => option.value === stewartProduct && !option.disabled);

  const submitAllowed = (transactionType && propertyTypeCode && stewartProduct && isStewartProductSelectable);

  const handleSubmit = async () => {
    const requiredLoanAmountProductList = generalConfig.titleSearchRequireLoanAmountProductList;
  
    if(requiredLoanAmountProductList?.includes(stewartProduct) && (!loanAmount || loanAmount <= 0)) {
      
      setError(`titleSearchProducts.${productIndex}.loanAmount`, {
        type: "required",
        message: `${LOAN_AMOUNT_REQUIRED_MESSAGE} ${requiredLoanAmountProductList.join(', ')}`
      });

      setFocus(`titleSearchProducts.${productIndex}.loanAmount`);
      return;
    }

    setNotDefault();
    onSubmit(titleSearchProduct.integrationKey, ProductAction.Issue);
  }

  const handleResendToStewart = async () => {
    setNotDefault();
    onSubmit(titleSearchProduct.integrationKey, ProductAction.IssueResend);
  }

  const handleUpdateOrder = async () => {
    setNotDefault();
    await onSubmit(titleSearchProduct.integrationKey, ProductAction.Revise);
    // if (actionResult && actionResult.status === ActionResultStatus.Success) {
    //   setTimeout(()=>{
    //     setIsOrderUpdateDialogOpen(false);  
    //   }, 100)

    //   // setIsOrderUpdateDialogOpen(false);
    // }
  }

  const handleResendToTPS = async () => {
    if (tpsName) {
      openAutomaticProgressDialog(`Please wait while resending to ${tpsName}.`);

      const scfile = getValues() as SCFile;
      const apiSCFile = mapUiSCFileToApiSCFile(
        initialValues,
        scfile,
        companyStateLocations, // undefined,
        MapActionType.TitleSearchResendToTPS,
      );

      try {
        const result: ActionResult = await titleSearchResend(apiSCFile, orderID, tpsName, integrationKey);
        if (result.status === ActionResultStatus.Success) {
          showToast({
            type: "success",
            title: RESEND_TO_TPS_SUCCESS_MESSAGE.replace("{tpsName}", tpsName),
          });
        } else {
          showToast({
            type: "error",
            title: RESEND_TO_TPS_FAIL_MESSAGE.replace("{tpsName}", tpsName),
          });
        }
      } catch (err) {
        showToast({
          type: "error",
          title: RESEND_TO_TPS_FAIL_MESSAGE.replace("{tpsName}", tpsName),
        });
      }
      finally {
        closeAutomaticProgressDialog();
      }

    }
  }

  const handleViewOrderSummary = async () => {
    const actionResult: ActionResult = await getTitleSearchOrderSummaryDocumentUrl(
      getValues(`titleSearchProducts.${productIndex}.orderID`),
      getValues("id"),
      uuidv4()
    );

    setPdfDocuments([
      getPdfDocument(
        undefined,
        undefined,
        undefined,
        actionResult.documentId || 0,
        actionResult.documentInfo,
        actionResult.documentName || "orderSummary",
        0
      ),
    ]);

    setOpenPdfViewer(true);
  }

  // Upload related function
  const handleOnCloseProgressDialog = async () => {
    isProgressCompletedRef.current = true;
    if (uploadCompleted) {
      setOpenProgressDialog(false);
      setRunProgress(false);
      setProgressRequestId("");
    }
  }

  useEffect(() => {
    if (uploadCompleted && openProgressDialog) {
      handleOnCloseProgressDialog();
    }
  }, [uploadCompleted])

  useEffect(() => {
    if (readyToSearch) {
      const search = async () => {
        await performSearch();
        setReadyToSearch(false);
      };

      search();
    }
  }, [readyToSearch]);

  useEffect(() => {
    if (canSearch()) {
      if (partialFilterUpdate) {
        setPartialFilterUpdate(false);
      }

      debounceSetReadyToSearch();
    }
  }, [
    filters,
    sortColumn,
    settingsLoaded,
  ]);

  useEffect(() => {
    const defaultFilters = getDefaultFilters(false);
    setFilters(defaultFilters);
  }, []);

  useEffect(() => {
    const init = async () => {
      await loadSettings();
      setSettingsLoaded(true);
    };

    init();
  }, []);

  useEffect(() => {
    setResendToTPSButton(data);
  }, [data, currentProduct?.orderStatusTypeCode])

  return (
    <>
      <CollapsableFormSection
        key={key}
        index={productIndex}
        showDelete={!disableDeleteButtom && !isProductStatusVisible} //showDelete()}
        onDelete={onDelete}
        showRefresh={isProductStatusVisible}
        onRefresh={handleRefresh}
        label={getTabDescription()}
        title={tabTitle}
        isProductIssuePending={
          titleSearchProduct?.orderStatusTypeCode === OrderStatusType.Pending
        }
      >
        <GridContainer>
          <Grid container={true} spacing={3}>
            {isProductStatusVisible && (
              <ProductStatus
                handleRefresh={handleRefresh}
                orderCreatedDate={orderCreatedDate}
                externalFileNumber={currentProduct?.externalFileNumber}
                orderStatusTypeName={orderStatus}
                currentProduct={currentProduct}
                isFieldDisabled={isFieldDisabled}
              />
            )}
            <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
              <TransactionTypeField
                schema={`titleSearchProducts.${productIndex}`}
                name={`titleSearchProducts.${productIndex}.transactionType`}
                disabled={isFieldDisabled}
                onChange={setNotDefault}
              />
            </Grid>
            <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
              <PropertyTypeField
                schema={`titleSearchProducts.${productIndex}`}
                name={`titleSearchProducts.${productIndex}.propertyTypeCode`}
                disabled={isFieldDisabled}
                onChange={setNotDefault}
              />
            </Grid>
            <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
              <ProductTypeField
                schema={`titleSearchProducts.${productIndex}`}
                name={`titleSearchProducts.${productIndex}.stewartProduct`}
                disabled={isFieldDisabled}
                onChange={setNotDefault}
              />
            </Grid>
            <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
              <CurrencyInputField
                label="Purchase Price"
                name={`titleSearchProducts.${productIndex}.purchasePrice`}
                disabled={isFieldDisabled}
                allowNegative={false}
                maxNumberOfDigits={29} // limit to enter number upto 19.2 + $ sign
                onChange={setNotDefault}
              />
            </Grid>
            <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
              <CurrencyInputField
                label="Loan Amount"
                name={`titleSearchProducts.${productIndex}.loanAmount`}
                disabled={isFieldDisabled}
                allowNegative={false}
                maxNumberOfDigits={29} // limit to enter number upto 19.2 + $ sign
                onChange={setNotDefault}
              />
            </Grid>
            <Grid item={true} xs={12} sm={6} md={4} lg={4} xl={4}>
              <NeedByDateField
                name={`titleSearchProducts.${productIndex}.needByDate`}
                disabled={isFieldDisabled}
                onChange={onNeedByDateChange}
                defaultValue={undefined}
              // forceRerender={forceRerenderNeedByDate}
              />
            </Grid>
            {/* <Grid item={true} xs={12} md={10} lg={10} xl={10}>
              <ProductAgencyLocationField
                label="Agency Location"
                productType={ProductType.TitleSearch}
                schema={`titleSearchProducts.${productIndex}`}
                name={`titleSearchProducts.${productIndex}.pricingDetail.locationDisplayName`}
                disabled={isFieldDisabled}
                onChange={onLocationChange}
                // isRevising={reviseState.revising}
              />
            </Grid> */}
            <Grid
              item={true}
              xs={!isProductStatusVisible ? 11.5 : 12}
              md={!isProductStatusVisible ? 11.5 : 12}
              lg={!isProductStatusVisible ? 11.5 : 12}
              xl={!isProductStatusVisible ? 11.5 : 12}
            >
              <NotificationEmailAddresses
                schema={`titleSearchProducts.${productIndex}`}
                name={`titleSearchProducts.${productIndex}.notificationEmailAddresses`}
                isTitleSearchDisabled={isTitleSearchDisabled}
                disabled={isFieldDisabled}
              />
            </Grid>
            {!isProductStatusVisible && (
              <Grid item={true} xs={0.5} md={0.5} lg={0.5} xl={0.5}>
                <EmailTooltipContainer>
                  <CustomTooltip
                    title={EMAIL_MESSAGE}
                    placement="left"
                    aria-label="lender-contact-info"
                    arrow
                  >
                    <StyledInfoIcon />
                  </CustomTooltip>
                </EmailTooltipContainer>
              </Grid>
            )}
            <Grid item={true} xs={12} md={12} lg={12} xl={12}>
              {!isProductStatusVisible && <TextInputField
                name={`titleSearchProducts.${productIndex}.messages.0.messageText`}
                placeholder="Message"
                label="Message"
                onChange={setNotDefault}
                style={{ position: "relative" }}
                disabled={isFieldDisabled}
                inputProps={{
                  maxLength: 2500,
                  minLength: 0,
                }}
              />}
            </Grid>
            {isProductStatusVisible && < MessageGrid disabled={isTitleSearchDisabled || isFileDisabled} productIndex={productIndex} isProductStatusVisible={isProductStatusVisible} />}
            {!isProductStatusVisible && (
              <Grid item={true} xs={12} md={12} lg={12} xl={12}>
                <UploadDropZone
                  documentType={DocumentType.TITLEDOCS}
                  // disabled={uploadDisabled} // according to Swaroop always enabled
                  disabled={isTitleSearchDisabled || isTitleSearchGridInEditMode || isFileDisabled}
                  handleOnUpload={handleOnUpload}
                />
              </Grid>
            )}
            {data && data.length > 0 && (
              <Grid rowSpacing={2} item={true} xs={12} md={12} lg={12} xl={12}>
                {selectedTitleSearchGridItems?.filter((d) => d.orderID === orderID)?.length > 0 && (
                  <MultipleDownloadHeader disabled={false}  orderID={orderID} onDownload={handleDownloadZipfile} />
                )}

                <UploadedDocumentsGrid
                  disabled={isTitleSearchDisabled || isFileDisabled}
                  selectionConfig={selectionConfig}
                  processedStatusTypeCode={processedStatusTypeCode}
                  page={1}
                  rows={data ? data : []}
                  orderBy={orderBy}
                  order={order}
                  noRowsMessage={noRecordsMessage}
                  onRequestSort={handleRequestSort}
                  performSearch={performSearch}
                  reloadFile={handleOnReloadFile}
                  reloadFileActivities={handleOnReloadFileActivities}
                  saveFile={handleOnSaveFile}
                />
              </Grid>
            )}

            {isProductStatusVisible && (
              <Grid item={true} xs={12} md={12} lg={12} xl={12}>
                <SendMessageDocument
                  disabled={isTitleSearchDisabled || isFileDisabled}
                  productIndex={productIndex}
                  parentIntegrationKey={titleSearchProduct.integrationKey}
                  performSearchDocument={performSearch}
                />
              </Grid>
            )}
            <Grid item={true} xs={12} md={12} lg={12} xl={12}>
              <ButtonsContainer>
                {isSubmitVisible && (
                  <ActionButton
                    color="primary"
                    disabled={
                      isTitleSearchDisabled ||
                      submitButtonDisabled ||
                      !submitAllowed ||
                      isProductStatusVisible ||
                      isTitleSearchGridInEditMode ||
                      isFileDisabled
                    }
                    onClick={handleSubmit}
                    variant="outlined"
                  >
                    Submit
                  </ActionButton>
                )}
                {hasResendPermissionAccess && isResendToReswareVisible && (
                  <ActionButton
                    color="secondary"
                    disabled={
                      isTitleSearchDisabled ||
                      resendToStewartButtonDisabled ||
                      isFileDisabled
                    }
                    onClick={handleResendToStewart}  // TODO: rlo 7/25/2025 -- similar like issue (submit) but will use ProductAction = "IssueResend"
                    startIcon={<ResendIcon />}
                    variant="outlined"
                  >
                    Resend to Stewart
                  </ActionButton>
                )}
                {hasResendPermissionAccess && isResendToTPSVisible && (
                  <ActionButton
                    color="secondary"
                    disabled={isTitleSearchDisabled || isFileDisabled}
                    onClick={handleResendToTPS}
                    startIcon={<ResendIcon />}
                    variant="outlined"
                  >
                    Resend to {capitalize(tpsName)}
                  </ActionButton>
                )}
                {isOrderUpdateVisible && (
                  <ActionButton
                    color="primary"
                    disabled={isTitleSearchDisabled || isFileDisabled}
                    onClick={() => {
                      setIsOrderUpdateDialogOpen(true);
                    }}
                    startIcon={<OrderUpdateIcon />}
                    variant="outlined"
                  >
                    Order Update
                  </ActionButton>
                )}
                {isCancelTitleSearchVisible && (
                  <>
                    <ActionButton
                      color="secondary"
                      disabled={isTitleSearchDisabled || isFileDisabled}
                      onClick={() => setCancelDialogOpen(true)}
                      startIcon={<CancelIcon />}
                      variant="outlined"
                    >
                      Cancel
                    </ActionButton>

                    <CancelTitleSearchDialog
                      schema={`titleSearchProducts.${productIndex}`}
                      onSubmit={handleCancelSubmit}
                      isOpen={isCancelDialogOpen}
                      onClose={() => {
                        setCancelDialogOpen(false);
                      }}
                      productIndex={productIndex}
                    />
                  </>
                )}
                {isCancelOrderUpdateVisible && (
                  <>
                    <ActionButton
                      color="secondary"
                      disabled={isTitleSearchDisabled || isFileDisabled}
                      onClick={() => setCancelUpdateDialogOpen(true)}
                      startIcon={<CancelIcon />}
                      variant="outlined"
                    >
                      Cancel Update
                    </ActionButton>
                  </>
                )}
                {/* {titleSearchProduct?.orderStatusTypeCode === OrderStatusType.Cancelled && (
                  <>
                    <ActionButton
                      color="primary"
                      onClick={() => console.log(" undoCancel clicked")}
                      startIcon={<UndoIcon />}
                      variant="outlined"
                    >
                      Undo Cancellation
                    </ActionButton>
                  </>
                )} */}
                {isProductStatusVisible && (
                  <ActionButton
                    color="primary"
                    onClick={handleViewOrderSummary}
                    startIcon={<PrinterEyeIcon />}
                    variant="outlined"
                    disabled={currentProduct?.orderStatusTypeCode === OrderStatusType.Pending}
                  >
                    View Order Summary
                  </ActionButton>
                )}

              </ButtonsContainer>
            </Grid>
          </Grid>
        </GridContainer>
      </CollapsableFormSection>
      <ConfirmationDialogWithProgressbar
        autoClose={true}
        title="Please wait while uploading your documents ..."
        closeButtonText="Done! Click to Continue"
        isOpen={openProgressDialog}
        onClose={handleOnCloseProgressDialog}
        requestId={progressRequestId}
        runProgress={runProgress}
        progressImageType={ProgressImageType.circular}
        delay={1000} // 1000 miliseconds - call processStatus every 1 second 
        maxDelayCounter={90} // max delay: 90 x 1000 miliseconds = 90 seconds - close progress maximum in 90 seconds + response time
        maxZeroPctCompleteCount={30} //  close 0 percentage dialog after about 30 seconds + response times for each call; where 30 count x delay (1000 miliseconds ) + response for each call
      />
      <UploadFailedDialog
        ref={uploadFailedDialogRef}
        failedUploadFilesRef={failedUploadFilesRef}
      />
      <ErrorDialog
        isOpen={openErrorDialog}
        confirmationMessage={errorMessage}
        correlationId={correlationId}
        onYes={handleOnYesErrorDialog}
      />

      <PdfViewer
        isOpen={openPdfViewer}
        onClose={() => setOpenPdfViewer(false)}
        pdfDocuments={pdfDocuments}
      />
      <OrderUpdateDialog
        ref={ref} // {orderUpdateDialogRef}
        onSubmit={handleUpdateOrder}
        titleSearchProduct={titleSearchProduct}
        isOpen={isOrderUpdateDialogOpen}
        onClose={() => {
          setIsOrderUpdateDialogOpen(false);
        }}
        productIndex={productIndex}
      />

      {isCancelUpdateDialogOpen && (
        <CancelTitleSearchDialog
          schema={`titleSearchProducts.${productIndex}`}
          isOpen={isCancelUpdateDialogOpen}
          onClose={() => {
            setCancelUpdateDialogOpen(false);
            setValue(
              `titleSearchProducts.${productIndex}.messageIntegrationKey`,
              ''
            )
            setMessageIntegrationKey("");
          }}
          onSubmit={handleCancelOrderUpdate}
          productIndex={productIndex}
          title="Cancel Title Search Update?"
          buttonText="Cancel Title Search Update"
        />
      )}

      {/*  DEBUG ONLY */}
      {/* <ActionButton
        color="primary"
        variant="outlined"
        onClick={handleOnDebugClick}
      >
        Show Warning Message
      </ActionButton> */}
    </>
  );
}

export default forwardRef<OrderUpdateDialogHandle, Props>(TitleSearch);
